import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { ProductExemptionDataType } from "@/formTypes/ProductExemptionDataType";
import { CabysLookup } from "@utils/cabysCatalog";
import { isValidCabysCode, normalizeCabysCode, productTypeFromCabys } from "@utils/Cabys";
import { isRateJustifiedByRegime } from "@utils/ReceptorTaxRegime";
import {
    ATV_SERVICES_SIZING_UNID,
    EINVOICE_EXPORT_INVOICE_TYPE_CODE,
    MERCHANDISE_PRODUCT_TYPE_CODE,
    PRODUCT_TYPES,
    SERVICE_PRODUCT_TYPE_CODE,
} from "@utils/Constants";
import { toNumberFormat } from "@utils/Utils";

/**
 * Por que una linea puede provocar un rechazo de Hacienda.
 *
 * - `cabys_missing`: la linea salio sin CAByS.
 * - `cabys_invalid`: el CAByS no tiene los 13 digitos.
 * - `cabys_unknown`: Hacienda respondio que ese codigo no existe en el catalogo.
 * - `type_mismatch`: el tipo con que se facturo no es el que le toca al CAByS.
 * - `tax_rate_mismatch`: la tarifa facturada no es la que el catalogo le asigna al CAByS.
 * - `exempt_mismatch`: se facturo exenta / sin impuesto una linea cuyo CAByS si grava.
 */
export type LineIssue =
    | "cabys_missing"
    | "cabys_invalid"
    | "cabys_unknown"
    | "type_mismatch"
    | "tax_rate_mismatch"
    | "exempt_mismatch";

export const LINE_ISSUE_LABELS: Record<LineIssue, string> = {
    cabys_missing: "Sin CAByS",
    cabys_invalid: "CAByS invalido",
    cabys_unknown: "CAByS inexistente",
    type_mismatch: "Tipo incorrecto",
    tax_rate_mismatch: "Tarifa incorrecta",
    exempt_mismatch: "Exenta indebida",
};

export const LINE_ISSUE_MESSAGES: Record<LineIssue, string> = {
    cabys_missing: "La linea se facturo sin codigo CAByS. Asignele uno al producto.",
    cabys_invalid: "El CAByS debe tener 13 digitos.",
    cabys_unknown: "Hacienda no reconoce este CAByS. Busquelo de nuevo en el catalogo.",
    type_mismatch:
        "Hacienda clasifica la linea por el primer digito del CAByS (6, 7, 8 y 9 son servicios) y no coincide con el tipo con que se facturo. Por eso los totales de mercancias y servicios no le cuadran.",
    tax_rate_mismatch:
        "La tarifa de IVA con que se facturo no es la que el catalogo de Hacienda le asigna a este CAByS. Hacienda publica esa tarifa solo como referencia: si la ley fija otra para esta venta, puede emitir igual.",
    exempt_mismatch:
        "La linea se facturo exenta (sin IVA) pero su CAByS si grava. Si el cliente tiene una exoneracion (Exonet, zona franca, diplomatico, municipalidad), registrela con \"Agregar Exoneracion\" en lugar de marcar la linea exenta; si no, corrija la tarifa del producto.",
};

export type LineDiagnosticRow = {
    /** Consecutivo 1-based: es la "linea" que nombra el mensaje de Hacienda. */
    lineNumber: number;
    /** Codigo comercial del producto, para ubicarlo en el catalogo. */
    codigo: string;
    productId?: string;
    description: string;
    /** El CAByS que viajo a Hacienda (el del catalogo de productos). */
    cabys: string;
    /** El CAByS congelado en la linea, solo cuando difiere del anterior. */
    lineCabys?: string;
    sentProductType: number;
    expectedProductType: number;
    /** Tarifa facturada, en porcentaje ("13", "0", "4"). */
    sentTaxRate: number;
    /** Tarifa del catalogo de Hacienda. `undefined` = no se pudo consultar. */
    catalogTaxRate?: number;
    exento: boolean;
    subtotal: number;
    issues: LineIssue[];
};

export const productTypeLabel = (type: number): string =>
    PRODUCT_TYPES[type] || "Sin tipo";

/**
 * El CAByS que realmente viaja a Hacienda.
 *
 * `generateDocumentProducs` (`src/atv/submitInvoice.ts`) manda `Product.codigo_cabys`, es decir
 * el del catalogo de productos y no el que quedo congelado en la linea, asi que el diagnostico
 * tiene que mirar el mismo.
 */
const sentCabysOf = (line: EDocumentProductDataType): string =>
    normalizeCabysCode(line.product?.codigo_cabys || line.codigo_cabys);

const lineCabysOf = (line: EDocumentProductDataType): string =>
    normalizeCabysCode(line.codigo_cabys);

/** Como clasifica el sistema hoy (ver `addEDocumentCalculateFields`). */
export const isServiceAsSent = (line: EDocumentProductDataType): boolean =>
    ATV_SERVICES_SIZING_UNID.includes(`${line.unidad_medida}`) ||
    Number(line.tipo_producto) === SERVICE_PRODUCT_TYPE_CODE;

/** Como clasifica Hacienda: por el primer digito del CAByS. */
export const isServiceByCabys = (line: EDocumentProductDataType): boolean =>
    productTypeFromCabys(sentCabysOf(line)) === SERVICE_PRODUCT_TYPE_CODE;

const isLineExempt = (line: EDocumentProductDataType): boolean =>
    Boolean(line.exento) || Number(line.impuestos || 0) === 0;

/** Contexto del documento que cambia la tarifa esperada de las lineas. */
export type LineDiagnosticOptions = {
    /** `tax_regime` del documento: tarifa especial por quien compra (ver `RECEPTOR_TAX_REGIMES`). */
    receptorTaxRegime?: string | null;
    /** `einvoice_kind` del documento. La exportacion esta exenta sin importar el CAByS. */
    documentType?: string | null;
};

/**
 * Diagnostico de todas las lineas del documento, no solo las que tienen problemas: el mensaje
 * de Hacienda menciona el numero de linea, y esa linea puede estar correcta.
 */
export const buildLineDiagnostics = (
    lines: EDocumentProductDataType[],
    cabysInfo: CabysLookup,
    options: LineDiagnosticOptions = {}
): LineDiagnosticRow[] =>
    (lines || []).map((line, index) => {
        const cabys = sentCabysOf(line);
        const lineCabys = lineCabysOf(line);
        const catalogEntry = cabysInfo.get(cabys);
        const catalogTaxRate = catalogEntry ? Number(catalogEntry.impuesto) : undefined;
        const sentTaxRate = Number(line.porcentaje_impuesto || 0);
        const exento = isLineExempt(line);

        const sentProductType = isServiceAsSent(line)
            ? SERVICE_PRODUCT_TYPE_CODE
            : MERCHANDISE_PRODUCT_TYPE_CODE;
        const expectedProductType = productTypeFromCabys(cabys);

        const issues: LineIssue[] = [];

        if (!cabys) {
            issues.push("cabys_missing");
        } else if (!isValidCabysCode(cabys)) {
            issues.push("cabys_invalid");
        } else if (catalogEntry === null) {
            issues.push("cabys_unknown");
        }

        if (isValidCabysCode(cabys) && sentProductType !== expectedProductType) {
            issues.push("type_mismatch");
        }

        // La tarifa del CAByS es la general del bien: la exportacion (Ley del IVA, art. 8.1) y
        // las tarifas por receptor (art. 11) la cambian legitimamente.
        const isExport = `${options.documentType ?? ""}` === EINVOICE_EXPORT_INVOICE_TYPE_CODE;
        if (catalogTaxRate !== undefined && !isExport) {
            if (catalogTaxRate > 0 && exento) {
                issues.push("exempt_mismatch");
            } else if (
                catalogTaxRate !== sentTaxRate &&
                !isRateJustifiedByRegime(catalogTaxRate, sentTaxRate, options.receptorTaxRegime)
            ) {
                issues.push("tax_rate_mismatch");
            }
        }

        return {
            lineNumber: index + 1,
            codigo: `${line.codigo || line.product?.codigo || ""}`,
            productId: line.productId ?? undefined,
            description: `${line.description || ""}`,
            cabys,
            lineCabys: lineCabys && lineCabys !== cabys ? lineCabys : undefined,
            sentProductType,
            expectedProductType,
            sentTaxRate,
            catalogTaxRate,
            exento,
            subtotal: Number(line.subtotal || 0),
            issues,
        };
    });

/**
 * Problemas que justifican bloquear una emision.
 *
 * `cabys_unknown` queda fuera a proposito: sale de que Hacienda devolvio un arreglo vacio, y
 * una respuesta rara del catalogo no puede impedir facturar. En el diagnostico de un rechazo
 * si se reporta, porque ahi es informacion y no un bloqueo.
 */
const PREFLIGHT_BLOCKING_ISSUES: LineIssue[] = [
    "cabys_missing",
    "cabys_invalid",
    "type_mismatch",
    "exempt_mismatch",
];

/**
 * Problemas que se advierten pero el usuario puede confirmar. Hacienda publica la tarifa del
 * CAByS solo como referencia y la ley tiene casos que el sistema no puede deducir (salud
 * privada, insumos agro a un comprador no inscrito, umbrales del art. 8).
 */
const PREFLIGHT_WARNING_ISSUES: LineIssue[] = ["tax_rate_mismatch"];

const filterPreflightRows = (
    lines: EDocumentProductDataType[],
    cabysInfo: CabysLookup,
    options: LineDiagnosticOptions,
    kinds: LineIssue[]
): LineDiagnosticRow[] =>
    buildLineDiagnostics(lines, cabysInfo, options)
        .map((row) => ({
            ...row,
            issues: row.issues.filter((issue) => kinds.includes(issue)),
        }))
        .filter((row) => row.issues.length > 0);

/** Solo las lineas que no deberian emitirse; es lo que usa la validacion previa a emitir. */
export const findPreflightLineIssues = (
    lines: EDocumentProductDataType[],
    cabysInfo: CabysLookup,
    options: LineDiagnosticOptions = {}
): LineDiagnosticRow[] => filterPreflightRows(lines, cabysInfo, options, PREFLIGHT_BLOCKING_ISSUES);

/** Lineas que se pueden emitir solo si el usuario confirma la diferencia. */
export const findPreflightLineWarnings = (
    lines: EDocumentProductDataType[],
    cabysInfo: CabysLookup,
    options: LineDiagnosticOptions = {}
): LineDiagnosticRow[] => filterPreflightRows(lines, cabysInfo, options, PREFLIGHT_WARNING_ISSUES);

/* ==================== Reconciliacion de totales ==================== */

/**
 * Codigos de tarifa que Hacienda contabiliza como "No Sujeto" y no como "Exento".
 *
 * El sistema manda "01" para toda linea al 0% (ver `TAXES_PERCENTAGES_CODE_BY_PERCENTAGE`) y
 * suma su monto a `TotalMercanciasExentas`, mientras Hacienda lo espera en `TotalMercNoSujeta`:
 * de ahi el par de rechazos "No Sujetas no coincide (X)" + "exentas no coincide (0)". La tarifa
 * verdaderamente exenta del catalogo v4.4 es la "10".
 */
const NOT_SUBJECT_TAX_RATE_CODES = ["01", "11"];

export type TotalsBucket =
    | "TotalServGravados"
    | "TotalServExentos"
    | "TotalServExonerado"
    | "TotalServNoSujeto"
    | "TotalMercanciasGravadas"
    | "TotalMercanciasExentas"
    | "TotalMercExonerada"
    | "TotalMercNoSujeta";

export const TOTALS_BUCKET_LABELS: Record<TotalsBucket, string> = {
    TotalServGravados: "Servicios gravados",
    TotalServExentos: "Servicios exentos",
    TotalServExonerado: "Servicios exonerados",
    TotalServNoSujeto: "Servicios no sujetos",
    TotalMercanciasGravadas: "Mercancias gravadas",
    TotalMercanciasExentas: "Mercancias exentas",
    TotalMercExonerada: "Mercancias exoneradas",
    TotalMercNoSujeta: "Mercancias no sujetas",
};

export type DocumentTotals = Record<TotalsBucket, number>;

export type TotalsReconciliationRow = {
    bucket: TotalsBucket;
    sent: number;
    expected: number;
    difference: number;
};

const emptyTotals = (): DocumentTotals => ({
    TotalServGravados: 0,
    TotalServExentos: 0,
    TotalServExonerado: 0,
    TotalServNoSujeto: 0,
    TotalMercanciasGravadas: 0,
    TotalMercanciasExentas: 0,
    TotalMercExonerada: 0,
    TotalMercNoSujeta: 0,
});

const parseExemption = (
    raw: EDocumentProductDataType["exemption"]
): ProductExemptionDataType | undefined => {
    if (!raw) return undefined;
    if (typeof raw !== "string") return raw;
    try {
        return JSON.parse(raw) as ProductExemptionDataType;
    } catch {
        return undefined;
    }
};

/**
 * Reparte las lineas en los buckets del comprobante.
 *
 * `isService` y `isNotSubject` se inyectan para poder correr el mismo calculo dos veces: una
 * replicando `addEDocumentCalculateFields` (lo que se envio) y otra con las reglas de Hacienda.
 */
const computeTotals = (
    lines: EDocumentProductDataType[],
    isService: (line: EDocumentProductDataType) => boolean,
    isNotSubject: (line: EDocumentProductDataType) => boolean
): DocumentTotals =>
    (lines || []).reduce((totals, line) => {
        const service = isService(line);
        let lineSubtotal = Number(line.subtotal || 0);

        const exemption = parseExemption(line.exemption);
        if (exemption) {
            const exonerado = Number(
                toNumberFormat(lineSubtotal * (Number(exemption.porcentaje_compra || 0) / 13))
            );
            lineSubtotal -= exonerado;
            totals[service ? "TotalServExonerado" : "TotalMercExonerada"] += exonerado;
        }

        if (isLineExempt(line)) {
            if (isNotSubject(line)) {
                totals[service ? "TotalServNoSujeto" : "TotalMercNoSujeta"] += lineSubtotal;
            } else {
                totals[service ? "TotalServExentos" : "TotalMercanciasExentas"] += lineSubtotal;
            }
        } else {
            totals[service ? "TotalServGravados" : "TotalMercanciasGravadas"] += lineSubtotal;
        }

        return totals;
    }, emptyTotals());

/** Los totales tal como los arma hoy `addEDocumentCalculateFields`. */
export const computeSentTotals = (lines: EDocumentProductDataType[]): DocumentTotals =>
    computeTotals(lines, isServiceAsSent, () => false);

/** Los totales como los recalcula Hacienda al validar el comprobante. */
export const computeExpectedTotals = (lines: EDocumentProductDataType[]): DocumentTotals =>
    computeTotals(lines, isServiceByCabys, (line) =>
        NOT_SUBJECT_TAX_RATE_CODES.includes(`${line.codigo_tarifa}`)
    );

/** Solo los buckets que no cuadran; si devuelve vacio el rechazo vino de otro lado. */
export const buildTotalsReconciliation = (
    lines: EDocumentProductDataType[]
): TotalsReconciliationRow[] => {
    const sent = computeSentTotals(lines);
    const expected = computeExpectedTotals(lines);

    return (Object.keys(sent) as TotalsBucket[])
        .map((bucket) => ({
            bucket,
            sent: sent[bucket],
            expected: expected[bucket],
            difference: sent[bucket] - expected[bucket],
        }))
        .filter((row) => Math.abs(row.difference) > 0.00001);
};

/* ==================== Receptor ==================== */

export type ReceptorIssueCode =
    | "missing_phone_country_code"
    | "missing_phone"
    | "missing_identification"
    | "missing_identification_type"
    | "missing_email";

export type ReceptorIssue = {
    code: ReceptorIssueCode;
    message: string;
};

/** `NumTelefono` es un entero con `minInclusive 100` en el XSD v4.4. */
const MIN_PHONE_DIGITS = 3;

const digitsOf = (raw?: string | null): string => `${raw ?? ""}`.replace(/\D/g, "");

export type ReceptorDiagnosticInput = {
    /** Telefono y codigo de pais congelados en el documento. */
    tel1?: string | null;
    tel1_cd?: string | null;
    identificacion?: string | null;
    tipo_identificacion?: string | null;
    email?: string | null;
};

export type ReceptorClientInput = {
    telefono1?: string | null;
    telefono1_cd?: string | null;
    identificacion?: string | null;
    tipo_identificacion?: string | null;
    email?: string | null;
};

/**
 * Datos del receptor que Hacienda rechaza por esquema.
 *
 * El telefono se valida contra el **maestro de clientes**, no contra el snapshot del documento:
 * la lambda que arma el XML emite `<CodigoPais>` y `<NumTelefono>` leyendo el cliente vigente,
 * asi que un documento con `tel1_cd` correcto igual sale mal si al cliente le falta el dato.
 */
export const buildReceptorDiagnostics = (
    document: ReceptorDiagnosticInput,
    client?: ReceptorClientInput | null
): ReceptorIssue[] => {
    const issues: ReceptorIssue[] = [];

    // El telefono se lee del maestro de clientes cuando el documento tiene uno: la lambda arma
    // `<CodigoPais>`/`<NumTelefono>` desde ahi, asi que el snapshot del documento no lo salva.
    // Sin cliente (factura de compra) el unico dato disponible es el del propio documento.
    const countryCode = digitsOf(client ? client.telefono1_cd : document.tel1_cd);
    const phone = digitsOf(client ? client.telefono1 : document.tel1);

    if (!countryCode) {
        issues.push({
            code: "missing_phone_country_code",
            message:
                "El cliente no tiene codigo de pais en su telefono. Hacienda recibe la etiqueta vacia y rechaza el comprobante por esquema. Para Costa Rica el codigo es 506.",
        });
    }

    if (phone.length < MIN_PHONE_DIGITS) {
        issues.push({
            code: "missing_phone",
            message:
                "El cliente no tiene un numero de telefono valido. Hacienda exige al menos 3 digitos en el numero del receptor.",
        });
    }

    // La identificacion y el correo si salen del snapshot del documento.
    if (!`${document.identificacion || client?.identificacion || ""}`.trim()) {
        issues.push({
            code: "missing_identification",
            message: "El cliente no tiene numero de identificacion (cedula).",
        });
    }

    if (!`${document.tipo_identificacion || client?.tipo_identificacion || ""}`.trim()) {
        issues.push({
            code: "missing_identification_type",
            message: "El cliente no tiene tipo de identificacion seleccionado.",
        });
    }

    if (!`${document.email || client?.email || ""}`.trim()) {
        issues.push({
            code: "missing_email",
            message:
                "El cliente no tiene correo electronico, asi que el comprobante no se le puede enviar.",
        });
    }

    return issues;
};

/**
 * Mensaje de bloqueo de la validacion previa a emitir.
 *
 * Nombra los codigos comerciales porque es el dato con el que el usuario encuentra el producto
 * en su catalogo; el numero de linea solo sirve dentro del documento.
 */
export const describePreflightIssues = (rows: LineDiagnosticRow[]): string => {
    const codes = rows.map((row) => row.codigo || `linea ${row.lineNumber}`).join(", ");
    const reasons = [...new Set(rows.flatMap((row) => row.issues))]
        .map((issue) => LINE_ISSUE_LABELS[issue].toLowerCase())
        .join(", ");

    return `Hacienda rechazaria este documento por el CAByS de ${rows.length === 1 ? "un producto" : "varios productos"} (${reasons}): ${codes}. Corrija el CAByS, el tipo de producto o la tarifa de IVA antes de facturar`;
};
